/**
 * Files: browse the device's file system, and move files either way.
 *
 * Every listing and every operation is a REST round trip that the server relays to the agent as a
 * SignalR client result, so a refused browse arrives as an ordinary answer with its own sentence in
 * it rather than as a transport failure - which is why nothing here treats a failure as an exception
 * unless the server itself could not be reached.
 *
 * **The bytes never ride that channel.** A download is fetched from its own endpoint and an upload is
 * posted to one, both with the bearer token, because the hub is sized for JPEG frames and a folder
 * of files has no business competing with the picture. The upload uses `XMLHttpRequest` for the one
 * thing `fetch` still cannot do - report request progress - and a 200 MB transfer with no feedback
 * is indistinguishable from a hang.
 *
 * Browsing runs as the agent, which is SYSTEM, so this reaches places a signed-in user cannot; the
 * card says so, and deletes skip the Recycle Bin.
 */
import type { Control } from 'webwin';
import * as crm from '../../api/crm.js';
import { describeTransferCap, maxFileEntries } from '../../api/settings.js';
import { describeError } from '../../api/http.js';
import { FileOperationKind, type FileBrowseResult, type FileEntryDto } from '../../contracts.gen.js';
import { download, pickFiles, uploadFiles } from '../../download.js';
import { bytes, join, localDateTime } from '../../format.js';
import { CardList } from '../../ui/CardList.js';
import { ask, confirm } from '../../ui/dialogs.js';
import { link } from '../../ui/Link.js';
import { BoundedScroller } from '../../ui/Scroller.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, fillWith, FONT, GAP, h2, hstack, inlineRow, muted, splitRow, stackOf, text, textBox, value, vstack } from '../../ui/layout.js';
import type { DeviceTab, DeviceTabContext } from './context.js';

/** The XAML's cap. The filter is how a bigger folder is navigated, and the overflow line says so. */
const MAX_FILE_ROWS = 250;

export interface FilesTab extends DeviceTab {
  applyPresence(): void;
}

export function filesTab(dc: DeviceTabContext): FilesTab {
  let listing: FileBrowseResult | null = null;
  let filter = '';
  let busy = false;
  /** Set once the explorer has been seeded, so revealing the tab again does not re-browse. */
  let seeded = false;

  const panel = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
  const status = new StatusLine();

  // ---- path bar ----
  const pathBox = textBox({ hAlign: 'stretch' });
  const goButton = button('Go', () => void browse(pathBox.text));
  const upButton = button('↑ Up', () => void browseParent());
  const drivesButton = button('Drives', () => void browse(''));
  const refreshButton = button('Refresh', () => void browse(listing?.path ?? ''));
  const pathRow = splitRow({ align: 'center' });
  fillWith(pathRow, pathBox);
  pathRow.controls.add(stackOf(buttonRow({ hAlign: 'end' }), goButton, upButton, drivesButton, refreshButton));

  // ---- folder actions ----
  const newFolderButton = button('New folder', () => void createFolder());
  const uploadButton = button('Upload files…', () => void upload());
  const location = muted('');
  const actionRow = stackOf(buttonRow(), newFolderButton, uploadButton, location);

  // ---- filter ----
  const filterBox = textBox({ hAlign: 'stretch' });
  filterBox.on('textchanged', () => {
    filter = filterBox.text;
    applyRows();
    dc.relayout();
  });

  const waiting = muted('Waiting for the device — the explorer loads while it is online.');
  const overflow = muted('');

  const rows = new CardList<FileEntryDto>({
    key: e => e.path || e.name,
    build: e => buildRow(e),
    max: MAX_FILE_ROWS,
    spacing: GAP.xs
  });
  const scroller = new BoundedScroller(340);
  scroller.content.controls.add(rows.panel);

  const body = card();
  body.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      pathRow,
      actionRow,
      filterBox,
      text('Filter by name', { role: 'muted', font: FONT.tiny }),
      status.label,
      waiting,
      scroller,
      overflow,
      text(
        `Browsing runs as the agent (SYSTEM) on the device. Transfers are capped at ${describeTransferCap()}; ` +
          'deletes are permanent and skip the Recycle Bin.',
        { role: 'muted', font: FONT.tiny, wrap: true }
      )
    )
  );

  panel.controls.addRange([h2('File explorer'), body]);

  // ---------------------------------------------------------------
  //  Rows
  // ---------------------------------------------------------------

  /** Collected as rows are built: every button here gates on presence, which moves under the page. */
  let rowButtons: Control[] = [];

  function buildRow(entry: FileEntryDto): Control {
    // The drive list has no path of its own, and a drive cannot be renamed or deleted - so those two
    // buttons are absent there rather than present and failing.
    const canModify = (listing?.path ?? '').length > 0;

    const left = inlineRow();
    left.controls.add(value(entry.isDirectory ? '📁' : '📄', { font: FONT.body }));
    // A folder navigates, so it is a link; a file is not clickable, so it is text. Rendering both as
    // links would offer a navigation that does nothing on half the rows.
    left.controls.add(
      entry.isDirectory
        ? link(entry.name, () => void browse(entry.path))
        : value(entry.name, { font: FONT.bold })
    );
    const detail = fileDetail(entry);
    if (detail) left.controls.add(muted(detail));

    const actions = buttonRow({ autoWidth: true, hAlign: 'end' });
    if (!entry.isDirectory) {
      const b = button('Download', () => void downloadEntry(entry), false, { enabled: dc.isOnline() });
      rowButtons.push(b);
      actions.controls.add(b);
    }
    if (canModify) {
      const rename = button('Rename', () => void renameEntry(entry), false, { enabled: dc.isOnline() });
      const remove = button('Delete', () => void deleteEntry(entry), false, { enabled: dc.isOnline() });
      rowButtons.push(rename, remove);
      actions.controls.addRange([rename, remove]);
    }

    const row = splitRow({ spacing: GAP.md, align: 'center' });
    fillWith(row, left);
    row.controls.add(actions);
    return row;
  }

  // ---------------------------------------------------------------
  //  Browsing
  // ---------------------------------------------------------------

  async function browse(path: string): Promise<void> {
    if (busy) return;
    busy = true;
    setToolbarEnabled(false);
    status.busy('Loading…');
    try {
      const result = await crm.browseDeviceFiles(dc.deviceId, path, dc.ctx.dispose.signal());
      if (dc.ctx.cancelled()) return;
      if (!result.success) {
        status.error(result.error ?? 'Could not browse the device.');
        return;
      }
      listing = result;
      seeded = true;
      pathBox.text = result.path;
      status.set(truncationNote(result));
      applyRows();
      waiting.visible = false;
      location.text = result.path.length > 0 ? result.path : 'Drives';
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    } finally {
      busy = false;
      setToolbarEnabled(true);
      dc.relayout();
    }
  }

  /** Up from a drive root is the drive list, which is what a null `parentPath` means. */
  function browseParent(): Promise<void> {
    return browse(listing?.parentPath ?? '');
  }

  function applyRows(): void {
    const all = listing?.entries ?? [];
    const needle = filter.trim().toLowerCase();
    const matched = needle ? all.filter(e => e.name.toLowerCase().includes(needle)) : all;
    rowButtons = [];
    rows.set(matched);
    overflow.text =
      matched.length > MAX_FILE_ROWS
        ? `Showing ${MAX_FILE_ROWS} of ${matched.length} — type in the filter to narrow the list.`
        : '';
    overflow.visible = overflow.text.length > 0;
    scroller.fit();
  }

  function setToolbarEnabled(enabled: boolean): void {
    const online = dc.isOnline() && enabled;
    goButton.enabled = online;
    upButton.enabled = online && listing !== null;
    drivesButton.enabled = online;
    refreshButton.enabled = online;
    // A new folder and an upload both need a real folder open. On the drive list there is nowhere to
    // put either, so they are disabled rather than failing at the device.
    const inFolder = (listing?.path ?? '').length > 0;
    newFolderButton.enabled = online && inFolder;
    uploadButton.enabled = online && inFolder;
    for (const b of rowButtons) (b as { enabled: boolean }).enabled = online;
  }

  // ---------------------------------------------------------------
  //  Operations
  // ---------------------------------------------------------------

  async function operate(
    kind: FileOperationKind,
    path: string,
    name: string | null,
    describe: (ok: boolean) => string
  ): Promise<void> {
    try {
      const result = await crm.deviceFileOperation(dc.deviceId, { kind, path, name });
      if (dc.ctx.cancelled()) return;
      if (result.success) {
        status.ok(describe(true));
        await browse(listing?.path ?? '');
        return;
      }
      status.error(result.error ?? describe(false));
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    }
    dc.relayout();
  }

  async function createFolder(): Promise<void> {
    const name = await ask(dc.ctx.desktop, {
      title: 'New folder',
      prompt: 'Name for the new folder:',
      initial: 'New folder'
    });
    if (!name || !name.trim() || dc.ctx.cancelled()) return;
    await operate(FileOperationKind.CreateDirectory, listing?.path ?? '', name.trim(), ok =>
      ok ? `Created "${name.trim()}".` : 'Could not create the folder.'
    );
  }

  async function renameEntry(entry: FileEntryDto): Promise<void> {
    const name = await ask(dc.ctx.desktop, {
      title: 'Rename',
      prompt: `New name for "${entry.name}":`,
      initial: entry.name
    });
    if (!name || !name.trim() || name === entry.name || dc.ctx.cancelled()) return;
    await operate(FileOperationKind.Rename, entry.path, name.trim(), ok =>
      ok ? `Renamed to "${name.trim()}".` : 'Could not rename.'
    );
  }

  async function deleteEntry(entry: FileEntryDto): Promise<void> {
    const ok = await confirm(dc.ctx.desktop, {
      title: 'Delete on device',
      message: entry.isDirectory
        ? `Delete the folder "${entry.name}" and everything in it from this device?\n\nThis cannot be undone.`
        : `Delete "${entry.name}" from this device?\n\nThis cannot be undone.`
    });
    if (!ok || dc.ctx.cancelled()) return;
    await operate(FileOperationKind.Delete, entry.path, null, done =>
      done ? `Deleted "${entry.name}".` : 'Could not delete.'
    );
  }

  /**
   * Streams one file to the browser.
   *
   * The device going offline half way through is a normal outcome here, so the failure is reported
   * rather than saved: a JSON error written to disk under the file's own name is worse than a
   * message, because it looks like a download that worked.
   */
  async function downloadEntry(entry: FileEntryDto): Promise<void> {
    status.busy(`Downloading ${entry.name}…`);
    try {
      await download(crm.deviceFileDownloadPath(dc.deviceId, entry.path), entry.name);
      if (dc.ctx.cancelled()) return;
      status.ok(`Downloaded ${entry.name}.`);
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    }
    dc.relayout();
  }

  async function upload(): Promise<void> {
    const target = listing?.path ?? '';
    if (target.length === 0) return;

    const files = await pickFiles();
    if (files.length === 0 || dc.ctx.cancelled()) return;

    status.busy(`Uploading ${files.length === 1 ? files[0]!.name : `${files.length} files`}…`);
    dc.relayout();
    try {
      await uploadFiles(crm.deviceFileUploadPath(dc.deviceId, target), files, fraction => {
        // Reported as a percentage rather than a bar: the transfer cap is configurable and can be
        // removed entirely, so the one number worth showing is how far along this transfer is.
        if (dc.ctx.cancelled()) return;
        status.busy(`Uploading… ${Math.round(fraction * 100)}%`);
      });
      if (dc.ctx.cancelled()) return;
      status.ok(`Uploaded ${files.length === 1 ? files[0]!.name : `${files.length} files`}.`);
      await browse(target);
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    }
    dc.relayout();
  }

  setToolbarEnabled(true);

  return {
    panel,
    /**
     * Seeds the drive list the first time this tab is opened.
     *
     * Deliberately not at page load, unlike the OpenSilver page which fired it from
     * `InitializeAsync`: it is a live round trip to the machine, and paying for one on every device
     * page opened - most of which are opened to read the Overview - is a cost with no reader.
     */
    onShow() {
      if (seeded || busy || !dc.isOnline()) return;
      void browse('');
    },
    applyPresence() {
      waiting.visible = listing === null;
      setToolbarEnabled(!busy);
    }
  };
}

/** The server's own detail string where it gave one (the drive list's free space), ours otherwise. */
function fileDetail(entry: FileEntryDto): string {
  if (entry.detail) return entry.detail;
  return join([
    entry.isDirectory ? null : bytes(entry.sizeBytes),
    entry.modifiedUtc ? `modified ${localDateTime(entry.modifiedUtc)}` : null
  ]);
}

/**
 * What to say about a capped listing.
 *
 * THE TWO CASES ARE WORDED APART, and that is the whole point of there being two flags. The ordinary
 * one drops only files, so every subfolder is listed and everything below this folder is still
 * reachable by clicking. The rare one drops subfolders, and part of the tree can then only be opened
 * by typing a path. Saying "some subfolders may be missing" in both cases would send a technician
 * hunting for a folder that is on screen.
 */
function truncationNote(result: FileBrowseResult): string {
  // The cap is a live setting, so the sentence asks for it rather than quoting a build-time copy.
  const cap = maxFileEntries();
  if (result.directoriesTruncated) {
    return (
      `This folder holds more than ${cap} subfolders — only the first ${cap} are listed, and the ` +
      'rest can only be opened by typing the path.'
    );
  }
  if (result.truncated) {
    return (
      `This folder holds more than ${cap} entries — every subfolder is listed, but only the first ` +
      'files are. Use the filter, or type a path.'
    );
  }
  return '';
}
